import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { KeyRound, ShieldAlert, Loader2, CheckCircle2, AlertTriangle, RotateCcw } from "lucide-react";
import { PageHeader } from "@/components/common/PageHeader";
import { Panel } from "@/components/common/Panel";

const CONFIRM_PHRASE = "MFA-RECOVERY";

function fmtRemaining(ms) {
  if (ms == null) return "—";
  if (ms <= 0) return "abgelaufen";
  const m = Math.floor(ms / 60000);
  const s = Math.floor((ms % 60000) / 1000);
  return `${m}:${String(s).padStart(2, "0")} min`;
}

export default function MfaRecovery() {
  const [step, setStep] = useState("idle"); // idle | started | done
  const [password, setPassword] = useState("");
  const [phrase, setPhrase] = useState("");
  const [ack, setAck] = useState(false);
  const [handle, setHandle] = useState(null);
  const [expiresAt, setExpiresAt] = useState(null);
  const [remaining, setRemaining] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!expiresAt) return;
    const end = new Date(expiresAt).getTime();
    const tick = () => setRemaining(end - Date.now());
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [expiresAt]);

  const reset = () => {
    setStep("idle"); setPassword(""); setPhrase(""); setAck(false);
    setHandle(null); setExpiresAt(null); setRemaining(null); setError(null);
  };

  const start = async () => {
    setError(null); setLoading(true);
    try {
      const { data } = await api.post("/admin/security/mfa-recovery/start", {
        password, confirm_phrase: phrase,
      });
      setHandle(data.handle);
      setExpiresAt(data.expires_at);
      setStep("started");
      setPassword(""); // don't keep the password in memory longer than needed
      toast.success("Recovery gestartet – bitte separat bestätigen.");
    } catch (e) {
      setError({ status: e.response?.status, msg: formatApiErrorDetail(e.response?.data?.detail) });
    } finally {
      setLoading(false);
    }
  };

  const confirm = async () => {
    // explicit, separate action only — no auto-confirm, no retry, no polling
    setError(null); setLoading(true);
    try {
      const { data } = await api.post("/admin/security/mfa-recovery/confirm", { handle });
      setStep("done");
      toast.success(data.message || "MFA-Recovery erfolgreich.");
    } catch (e) {
      setError({ status: e.response?.status, msg: formatApiErrorDetail(e.response?.data?.detail) });
    } finally {
      setLoading(false);
    }
  };

  const expired = remaining != null && remaining <= 0;
  const startDisabled = loading || !password || phrase.trim() !== CONFIRM_PHRASE;
  const confirmDisabled = loading || !ack || expired;

  return (
    <div data-testid="mfa-recovery-page" className="space-y-6">
      <PageHeader
        icon={KeyRound}
        title="MFA-Recovery"
        subtitle="Platform-Super-Admin · Notfall-Wiederherstellung der Zwei-Faktor-Authentisierung"
      />

      <Panel>
        <div data-testid="mfa-warning" className="mb-6 flex items-start gap-3 rounded-xl border border-status-warning-border bg-status-warning-bg p-4">
          <AlertTriangle className="mt-0.5 h-5 w-5 flex-shrink-0 text-status-warning" />
          <p className="text-sm text-fundio-ink">
            Nur verwenden, wenn der Platform-Super-Admin keinen Zugriff mehr auf seine Authenticator-App hat.
            Der Vorgang ist zweistufig und einmalig gültig.
          </p>
        </div>

        {step === "done" ? (
          <div data-testid="mfa-success" className="rounded-2xl border border-status-success-border bg-status-success-bg p-8 text-center">
            <CheckCircle2 className="mx-auto mb-3 h-10 w-10 text-status-success" />
            <p className="text-sm font-medium text-fundio-ink">
              MFA-Recovery erfolgreich. Alle bisherigen APP-Sessions wurden widerrufen.
            </p>
            <p className="mt-2 text-sm text-fundio-muted">
              Bitte jetzt bei app.fundio.ch neu anmelden und 2FA neu einrichten.
            </p>
            <button data-testid="mfa-restart-btn" onClick={reset} className="mt-6 inline-flex items-center gap-2 rounded-lg border border-fundio-border px-4 py-2 text-sm font-medium text-fundio-ink hover:border-fundio-gold">
              <RotateCcw className="h-4 w-4" /> Fertig
            </button>
          </div>
        ) : (
          <div className="grid gap-6 lg:grid-cols-2">
            {/* STEP A */}
            <div className={`rounded-2xl border p-6 ${step === "idle" ? "border-fundio-gold/50 bg-white" : "border-fundio-border bg-fundio-subtle opacity-70"}`}>
              <div className="mb-1 text-xs font-semibold uppercase tracking-wider text-fundio-gold-deep">Schritt A</div>
              <h3 className="mb-4 text-base font-semibold text-fundio-ink">Recovery starten</h3>
              <label className="mb-1 block text-xs font-medium text-fundio-muted">CONTROL-Passwort bestätigen</label>
              <input data-testid="mfa-start-password" type="password" value={password} disabled={step !== "idle"}
                onChange={(e) => setPassword(e.target.value)} autoComplete="current-password"
                className="mb-4 w-full rounded-lg border border-fundio-border bg-white px-3 py-2 text-sm outline-none focus:border-fundio-gold disabled:bg-fundio-subtle"
                placeholder="Ihr CONTROL-Passwort" />
              <label className="mb-1 block text-xs font-medium text-fundio-muted">Zur Bestätigung „{CONFIRM_PHRASE}" eingeben</label>
              <input data-testid="mfa-start-phrase" type="text" value={phrase} disabled={step !== "idle"}
                onChange={(e) => setPhrase(e.target.value)}
                className="mb-4 w-full rounded-lg border border-fundio-border bg-white px-3 py-2 font-mono text-sm outline-none focus:border-fundio-gold disabled:bg-fundio-subtle"
                placeholder={CONFIRM_PHRASE} />
              <button data-testid="mfa-start-btn" onClick={start} disabled={step !== "idle" || startDisabled}
                className="inline-flex items-center gap-2 rounded-lg bg-fundio-dark2 px-4 py-2 text-sm font-semibold text-fundio-cream hover:bg-fundio-dark disabled:cursor-not-allowed disabled:opacity-40">
                {loading && step === "idle" ? <Loader2 className="h-4 w-4 animate-spin" /> : <KeyRound className="h-4 w-4" />}
                MFA-Recovery starten
              </button>
            </div>

            {/* STEP B */}
            <div className={`rounded-2xl border p-6 ${step === "started" ? "border-fundio-gold/50 bg-white" : "border-fundio-border bg-fundio-subtle opacity-60"}`}>
              <div className="mb-1 flex items-center justify-between">
                <span className="text-xs font-semibold uppercase tracking-wider text-fundio-gold-deep">Schritt B</span>
                {step === "started" && (
                  <span data-testid="mfa-remaining" className={`text-xs font-medium ${expired ? "text-status-danger" : "text-fundio-muted"}`}>⏱ {fmtRemaining(remaining)}</span>
                )}
              </div>
              <h3 className="mb-4 text-base font-semibold text-fundio-ink">MFA-Recovery bestätigen</h3>
              {step !== "started" ? (
                <p className="text-sm text-fundio-muted">Zuerst Schritt A abschliessen.</p>
              ) : (
                <>
                  <div data-testid="mfa-confirm-warning" className="mb-4 flex items-start gap-2 rounded-lg border border-status-danger-border bg-status-danger-bg p-3">
                    <ShieldAlert className="mt-0.5 h-4 w-4 flex-shrink-0 text-status-danger" />
                    <p className="text-xs text-fundio-ink">
                      Durch die Bestätigung werden bestehende Sessions des Platform-Super-Admins widerrufen und beim
                      nächsten Login muss 2FA neu eingerichtet werden.
                    </p>
                  </div>
                  <label className="mb-4 flex items-start gap-2 text-sm text-fundio-ink">
                    <input data-testid="mfa-confirm-ack" type="checkbox" checked={ack} onChange={(e) => setAck(e.target.checked)} className="mt-0.5" />
                    <span>Ich verstehe die Konsequenzen und möchte die MFA-Recovery jetzt bestätigen.</span>
                  </label>
                  <button data-testid="mfa-confirm-btn" onClick={confirm} disabled={confirmDisabled}
                    className="inline-flex items-center gap-2 rounded-lg bg-status-danger px-4 py-2 text-sm font-semibold text-white hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-40">
                    {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShieldAlert className="h-4 w-4" />}
                    MFA-Recovery bestätigen
                  </button>
                  {expired && (
                    <button data-testid="mfa-restart-expired" onClick={reset} className="ml-3 inline-flex items-center gap-1 text-xs font-medium text-fundio-muted hover:underline">
                      <RotateCcw className="h-3.5 w-3.5" /> Neu starten
                    </button>
                  )}
                </>
              )}
            </div>
          </div>
        )}

        {error && (
          <div data-testid="mfa-error" className="mt-6 flex items-start gap-3 rounded-xl border border-status-danger-border bg-status-danger-bg p-4">
            <ShieldAlert className="mt-0.5 h-5 w-5 flex-shrink-0 text-status-danger" />
            <div className="text-sm text-fundio-ink">
              <span className="font-semibold">{error.status ? `Fehler ${error.status}: ` : ""}</span>{error.msg}
              {step === "started" && <button onClick={reset} className="ml-2 text-xs font-medium text-fundio-gold-deep hover:underline">Neu starten</button>}
            </div>
          </div>
        )}
      </Panel>
    </div>
  );
}
